import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { TopBar } from '../components/layout/TopBar';
import { useAuthStore } from '../store/useStore';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent } from '../components/ui/card';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import { cn } from '../lib/utils';
import {
  Code2, Copy, ExternalLink, Eye, Settings, Check, Loader2,
  Monitor, Smartphone, Tablet, QrCode, Zap, Layout, Type, Grid,
  Sparkles
} from 'lucide-react';
import WidgetIntegrationPanel from '../components/widgets/WidgetIntegrationPanel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LAYOUT_OPTIONS = [
  { value: 'grid',     label: 'Cuadrícula' },
  { value: 'list',     label: 'Lista' },
  { value: 'masonry',  label: 'Mosaico' },
];

const FONT_OPTIONS = [
  { value: 'inter',    label: 'Inter' },
  { value: 'poppins',  label: 'Poppins' },
  { value: 'playfair', label: 'Playfair Display' },
];

const DEFAULT_CONFIG = {
  primaryColor: '#F97316',
  backgroundColor: '#FFFFFF',
  textColor: '#1E293B',
  accentColor: '#FBBF24',
  layout: 'grid',
  fontFamily: 'inter',
  borderRadius: '12',
  showPrice: true,
  showDuration: true,
  showCategory: true,
  showDescription: true,
  showBookingButton: true,
  maxExperiences: 6,
  buttonText: 'Reservar ahora',
  buttonStyle: 'filled',
  theme: 'light',
  filterByCategory: 'all',
};

function ExperienceWidgetPreview({ experiences, config }) {
  const items = experiences
    .filter(e => config.filterByCategory === 'all' || e.category === config.filterByCategory)
    .slice(0, config.maxExperiences);
  const isDark = config.theme === 'dark';
  const br = `${config.borderRadius}px`;
  const fontFamily = { inter: "'Inter',sans-serif", poppins: "'Poppins',sans-serif", playfair: "'Playfair Display',serif" }[config.fontFamily] || "'Inter',sans-serif";

  const wrap = { backgroundColor: config.backgroundColor, color: config.textColor, fontFamily, padding: '28px', borderRadius: br, minHeight: 320, boxSizing: 'border-box' };
  const card = { backgroundColor: isDark ? '#1e293b' : '#ffffff', borderRadius: br, overflow: 'hidden', border: `1px solid ${isDark ? '#334155' : '#e2e8f0'}`, boxShadow: '0 1px 4px rgba(0,0,0,0.06)', display: 'flex', flexDirection: 'column' };
  const tag = (bg, color) => ({ fontSize: '11px', padding: '2px 10px', borderRadius: '100px', backgroundColor: bg, color, fontWeight: 600, display: 'inline-flex', alignItems: 'center' });

  const btn = {
    backgroundColor: config.buttonStyle === 'filled' ? config.primaryColor : 'transparent',
    color: config.buttonStyle === 'filled' ? '#fff' : config.primaryColor,
    border: config.buttonStyle === 'ghost' ? 'none' : `2px solid ${config.primaryColor}`,
    borderRadius: `calc(${config.borderRadius}px - 2px)`,
    padding: '9px 18px', fontSize: '13px', fontWeight: 600,
    cursor: 'pointer', width: '100%', marginTop: '10px', fontFamily,
  };

  const isList = config.layout === 'list';

  return (
    <div style={wrap}>
      {items.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '60px 20px', opacity: 0.4 }}>
          <div style={{ fontSize: '48px', marginBottom: '12px' }}>✨</div>
          <p style={{ fontSize: '14px', margin: 0 }}>No hay experiencias disponibles</p>
        </div>
      ) : (
        <div style={{ display: 'grid', gridTemplateColumns: isList ? '1fr' : 'repeat(auto-fill,minmax(210px,1fr))', gap: '16px' }}>
          {items.map(exp => (
            <div key={exp.id} style={{ ...card, flexDirection: isList ? 'row' : 'column' }}>
              <div style={{ background: `linear-gradient(135deg,${config.primaryColor}20,${config.accentColor || config.primaryColor}50)`, height: isList ? 'auto' : '120px', width: isList ? '100px' : '100%', minHeight: isList ? '80px' : 'auto', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '30px' }}>
                ✨
              </div>
              <div style={{ padding: '14px', flex: 1, display: 'flex', flexDirection: 'column' }}>
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '8px', marginBottom: '6px' }}>
                  <h3 style={{ margin: 0, fontSize: '14px', fontWeight: 700, color: config.textColor, lineHeight: 1.3 }}>{exp.name}</h3>
                  {config.showPrice && exp.price != null && (
                    <span style={{ fontSize: '15px', fontWeight: 800, color: config.primaryColor, flexShrink: 0 }}>
                      {exp.price === 0 ? 'Gratis' : `€${exp.price}`}
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '5px', marginBottom: '8px' }}>
                  {config.showCategory && exp.category && <span style={tag(`${config.primaryColor}15`, config.primaryColor)}>{exp.category}</span>}
                  {config.showDuration && exp.duration && <span style={tag(isDark ? '#334155' : '#f1f5f9', isDark ? '#94a3b8' : '#64748b')}>⏱ {exp.duration} min</span>}
                </div>
                {config.showDescription && exp.description && (
                  <p style={{ margin: '0 0 8px', fontSize: '12px', opacity: 0.65, lineHeight: 1.5, flex: 1 }}>
                    {exp.description.length > 80 ? exp.description.slice(0, 80) + '…' : exp.description}
                  </p>
                )}
                {config.showBookingButton && <button style={btn}>{config.buttonText}</button>}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

export default function ExperienceWidgetBuilder() {
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const [loading, setLoading] = useState(true);
  const [experiences, setExperiences] = useState([]);
  const [restaurant, setRestaurant] = useState(null);
  const [config, setConfig] = useState(DEFAULT_CONFIG);
  const [copied, setCopied] = useState(null);
  const [viewport, setViewport] = useState('desktop');

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [expRes, restRes] = await Promise.all([
        api.get('/experiences'),
        api.get('/restaurant'),
      ]);
      setExperiences(Array.isArray(expRes.data) ? expRes.data : []);
      setRestaurant(restRes.data);
    } catch { toast.error('Error al cargar datos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const setC = (key, value) => setConfig(c => ({ ...c, [key]: value }));

  const restaurantId = restaurant?.slug || user?.restaurant_id;

  const widgetUrl = (() => {
    if (!restaurantId) return '';
    const base = 'https://okreserva.okgo.mx';
    const params = new URLSearchParams({
      layout: config.layout,
      color: config.primaryColor.replace('#', ''),
      accent: config.accentColor.replace('#', ''),
      bg: config.backgroundColor.replace('#', ''),
      text: config.textColor.replace('#', ''),
      radius: config.borderRadius,
      font: config.fontFamily,
      max: config.maxExperiences,
      showPrice: config.showPrice,
      showDuration: config.showDuration,
      showCat: config.showCategory,
      showDesc: config.showDescription,
      btnText: encodeURIComponent(config.buttonText),
      btnStyle: config.buttonStyle,
      theme: config.theme,
      cat: config.filterByCategory,
    });
    return `${base}/widget/experiences/${restaurantId}?${params}`;
  })();

  const iframeCode = `<iframe
  src="${widgetUrl}"
  width="100%"
  height="${config.layout === 'list' ? '650' : '750'}"
  frameborder="0"
  style="border-radius: ${config.borderRadius}px; max-width: 960px; display: block; margin: 0 auto;"
  loading="lazy"
  title="Reserva de Experiencias"
></iframe>`;

  const scriptCode = `<div id="ok-exp-widget" 
  data-restaurant="${restaurantId}"
  data-config='${JSON.stringify(config)}'
></div>
<script src="${API_URL?.replace('/api', '')}/widget.js" async defer></script>`;

  const copyText = (text, key) => {
    navigator.clipboard.writeText(text);
    setCopied(key);
    toast.success('Copiado al portapapeles');
    setTimeout(() => setCopied(null), 2000);
  };

  const viewportW = { desktop: '100%', tablet: '768px', mobile: '390px' };

  const categories = [...new Set(experiences.map(e => e.category).filter(Boolean))];

  if (loading) return (
    <div className="min-h-screen flex flex-col">
      <TopBar title="Widget de Experiencias" />
      <div className="flex-1 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-orange-500" />
      </div>
    </div>
  );

  return (
    <div className="min-h-screen flex flex-col bg-slate-50">
      <TopBar
        title="Widget de Experiencias"
        actions={
          <div className="flex items-center gap-2">
            <Button size="sm" className="h-8 text-xs bg-orange-500 hover:bg-orange-600 text-white" onClick={() => { localStorage.setItem(`widget_exp_config_${restaurantId}`, JSON.stringify(config)); toast.success('Configuración guardada'); }}>
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Guardar
            </Button>
            <Button variant="outline" size="sm" onClick={() => navigate('/experiences')} className="h-8 text-xs">
              <Sparkles className="h-3.5 w-3.5 mr-1" /> Gestionar
            </Button>
            {widgetUrl && (
              <a href={widgetUrl} target="_blank" rel="noopener noreferrer">
                <Button variant="outline" size="sm" className="h-8 text-xs">
                  <ExternalLink className="h-3.5 w-3.5 mr-1" /> Ver en vivo
                </Button>
              </a>
            )}
          </div>
        }
      />

      <div className="flex flex-1 overflow-hidden">
        {/* Config panel */}
        <div className="w-72 bg-white border-r flex flex-col overflow-y-auto shadow-sm flex-shrink-0">
          <div className="p-4 border-b">
            <h2 className="font-semibold text-sm text-slate-700 flex items-center gap-2">
              <Settings className="h-4 w-4 text-orange-500" /> Configuración
            </h2>
            <p className="text-[11px] text-slate-400 mt-0.5">{experiences.length} experiencias disponibles</p>
          </div>

          <div className="p-4 space-y-5 flex-1">
            {/* Category filter */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Filtrar por categoría</Label>
              <Select value={config.filterByCategory} onValueChange={v => setC('filterByCategory', v)}>
                <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todas las categorías</SelectItem>
                  {categories.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>

            {/* Layout */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Distribución</Label>
              <div className="grid grid-cols-3 gap-1.5">
                {LAYOUT_OPTIONS.map(l => (
                  <button key={l.value} onClick={() => setC('layout', l.value)}
                    className={cn("py-2 rounded-lg border text-[11px] font-medium transition-all",
                      config.layout === l.value
                        ? "border-orange-500 bg-orange-50 text-orange-700"
                        : "border-slate-200 text-slate-500 hover:border-slate-300")}>
                    {l.label}
                  </button>
                ))}
              </div>
            </div>

            {/* Theme */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">Tema</Label>
              <div className="flex gap-2">
                {['light', 'dark'].map(t => (
                  <button key={t} onClick={() => setC('theme', t)}
                    className={cn("flex-1 py-1.5 rounded-lg border text-xs font-medium transition-all",
                      config.theme === t ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-500")}>
                    {t === 'light' ? '☀️ Claro' : '🌙 Oscuro'}
                  </button>
                ))}
              </div>
            </div>

            {/* Colors */}
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Colores</Label>
              <div className="space-y-2">
                {[
                  { key: 'primaryColor', label: 'Principal' },
                  { key: 'accentColor',  label: 'Acento' },
                  { key: 'backgroundColor', label: 'Fondo' },
                  { key: 'textColor',    label: 'Texto' },
                ].map(c => (
                  <div key={c.key} className="flex items-center justify-between">
                    <span className="text-xs text-slate-600">{c.label}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-[10px] text-slate-400 font-mono">{config[c.key]}</span>
                      <input type="color" value={config[c.key]} onChange={e => setC(c.key, e.target.value)}
                        className="h-7 w-9 rounded border border-slate-200 cursor-pointer p-0.5" />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Font + Radius */}
            <div className="space-y-3">
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block">Tipografía</Label>
                <Select value={config.fontFamily} onValueChange={v => setC('fontFamily', v)}>
                  <SelectTrigger className="text-xs h-8"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {FONT_OPTIONS.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs text-slate-500 mb-1.5 block">
                  Radio de bordes: {config.borderRadius}px
                </Label>
                <input type="range" min={0} max={32} value={config.borderRadius}
                  onChange={e => setC('borderRadius', e.target.value)}
                  className="w-full accent-orange-500" />
              </div>
            </div>

            {/* Toggles */}
            <div>
              <Label className="text-xs text-slate-500 mb-2 block">Mostrar elementos</Label>
              <div className="space-y-2">
                {[
                  { key: 'showPrice',         label: 'Precio' },
                  { key: 'showDuration',      label: 'Duración' },
                  { key: 'showCategory',      label: 'Categoría' },
                  { key: 'showDescription',   label: 'Descripción' },
                  { key: 'showBookingButton', label: 'Botón de reserva' },
                ].map(t => (
                  <label key={t.key} className="flex items-center justify-between cursor-pointer">
                    <span className="text-xs text-slate-600">{t.label}</span>
                    <Switch checked={config[t.key]} onCheckedChange={v => setC(t.key, v)} />
                  </label>
                ))}
              </div>
            </div>

            {/* Button */}
            <div className="space-y-2">
              <Label className="text-xs text-slate-500 block">Texto del botón</Label>
              <Input value={config.buttonText} onChange={e => setC('buttonText', e.target.value)}
                placeholder="Reservar ahora" className="text-xs h-8" />
              <div className="flex gap-2">
                {['filled', 'outline', 'ghost'].map(s => (
                  <button key={s} onClick={() => setC('buttonStyle', s)}
                    className={cn("flex-1 py-1.5 rounded-lg border text-[11px] font-medium capitalize transition-all",
                      config.buttonStyle === s ? "border-orange-500 bg-orange-50 text-orange-700" : "border-slate-200 text-slate-500")}>
                    {s === 'filled' ? 'Relleno' : s === 'outline' ? 'Borde' : 'Texto'}
                  </button>
                ))}
              </div>
            </div>

            {/* Max count */}
            <div>
              <Label className="text-xs text-slate-500 mb-1.5 block">
                Mostrar {config.maxExperiences} experiencia{config.maxExperiences !== 1 ? 's' : ''}
              </Label>
              <input type="range" min={1} max={20} value={config.maxExperiences}
                onChange={e => setC('maxExperiences', +e.target.value)}
                className="w-full accent-orange-500" />
              <div className="flex justify-between text-[10px] text-slate-400 mt-0.5">
                <span>1</span><span>20</span>
              </div>
            </div>
          </div>
        </div>

        {/* Preview + Code */}
        <div className="flex-1 flex flex-col overflow-hidden">
          <Tabs defaultValue="preview" className="flex flex-col flex-1 overflow-hidden">
            <div className="bg-white border-b px-4 flex items-center gap-2">
              <TabsList className="h-9 bg-transparent">
                <TabsTrigger value="preview" className="text-xs data-[state=active]:bg-slate-100 rounded-lg">
                  <Eye className="h-3.5 w-3.5 mr-1.5" /> Vista previa
                </TabsTrigger>
                <TabsTrigger value="code" className="text-xs data-[state=active]:bg-slate-100 rounded-lg">
                  <Code2 className="h-3.5 w-3.5 mr-1.5" /> Integración
                </TabsTrigger>
              </TabsList>
              <div className="ml-auto flex bg-slate-100 rounded-lg p-0.5 gap-0.5">
                {[
                  { k: 'desktop', icon: Monitor },
                  { k: 'tablet',  icon: Tablet },
                  { k: 'mobile',  icon: Smartphone },
                ].map(v => (
                  <button key={v.k} onClick={() => setViewport(v.k)}
                    className={cn("p-1.5 rounded-md transition-all",
                      viewport === v.k ? "bg-white shadow-sm text-slate-800" : "text-slate-400 hover:text-slate-600")}>
                    <v.icon className="h-3.5 w-3.5" />
                  </button>
                ))}
              </div>
            </div>

            <TabsContent value="preview" className="flex-1 overflow-auto p-6 m-0 flex justify-center bg-slate-100">
              <div className="transition-all duration-300 w-full" style={{ maxWidth: viewportW[viewport] === '100%' ? '100%' : viewportW[viewport] }}>
                <div className="mb-3 flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-3 h-3 rounded-full bg-red-400" />
                    <div className="w-3 h-3 rounded-full bg-yellow-400" />
                    <div className="w-3 h-3 rounded-full bg-green-400" />
                  </div>
                  <div className="flex-1 bg-white rounded-full px-3 py-1 text-[10px] text-slate-400 font-mono">
                    yourwebsite.com
                  </div>
                </div>
                <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-slate-200">
                  <ExperienceWidgetPreview experiences={experiences} config={config} />
                </div>
              </div>
            </TabsContent>
            <TabsContent value="code" className="flex-1 overflow-auto p-6 m-0 bg-slate-50">
              <div className="max-w-6xl mx-auto">
                <WidgetIntegrationPanel
                  type="experiences"
                  restaurantId={restaurantId || 'YOUR_RESTAURANT_ID'}
                  urlParams={{
                    layout: config.layout,
                    color: config.primaryColor.replace('#', ''),
                    accent: config.accentColor.replace('#', ''),
                    bg: config.backgroundColor.replace('#', ''),
                    text: config.textColor.replace('#', ''),
                    radius: config.borderRadius,
                    font: config.fontFamily,
                    maxExperiences: config.maxExperiences,
                    showPrice: config.showPrice,
                    showDuration: config.showDuration,
                    showCategory: config.showCategory,
                    showDescription: config.showDescription,
                    showBookingButton: config.showBookingButton,
                    btnStyle: config.buttonStyle,
                    theme: config.theme,
                    cat: config.filterByCategory,
                  }}
                  sdkConfig={config}
                  borderRadius={`${config.borderRadius}px`}
                  embedTitle="Widget de Experiencias"
                />
              </div>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
}
